<template>
  <CardWithTitle
    :items="items"
    :item-id="itemId"
    :map-keys="mapKeys"
    :request-status="requestStatus"
    :options-data="cardOptions"
    :has-data-to-export="hasDataToExport"
    @call-option-menu="callOptionMenu"
    @reload="getData"
  >
    <v-data-table
      v-if="hasItems"
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      class="table-truncate"
    >
      <template v-slot:[`item.id`]="{ item }">
        <span>{{ item.botId }}</span>
      </template>
      <template v-slot:[`item.report`]="{ item }">
        <span>{{ item.report }}</span>
      </template>
      <template v-slot:[`item.closedTickets`]="{ item }">
        <span>{{ item.closedTickets }}</span>
      </template>
      <template v-slot:[`item.averageFirstResponseTime`]="{ item }">
        <span>{{ item.averageFirstResponseTime }}</span>
      </template>
      <template v-slot:[`item.averageWaitTime`]="{ item }">
        <span>{{ item.averageWaitTime }}</span>
      </template>
      <template v-slot:[`item.averageResponseTime`]="{ item }">
        <span>{{ item.averageResponseTime }}</span>
      </template>
    </v-data-table>
  </CardWithTitle>
</template>

<script>
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import { mapActions } from 'vuex';
import blipModel from '@/mixins/blip-model';
import exportModel from '@/mixins/export-model';
import cardsModel from '@/mixins/cards-model';
import { remapFieldKeys } from '@/utils/formatter';
import { saveAs } from 'file-saver';
import XLSX from 'xlsx';

export default {
  name: 'BlipReportsTable',
  components: {
    CardWithTitle,
  },
  mixins: [
    blipModel,
    exportModel,
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    servicePath: {
      type: String,
      required: true,
    },
    reportType: {
      type: String,
      required: true,
    },
    hasDataToExport: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      reportsTypes: {
        attendants: {
          header: this.$t('components.BlipReportsTable.attendents'),
          value: 'identity',
        },
        teams: {
          header: this.$t('components.BlipReportsTable.teams'),
          value: 'name',
        },
        tags: {
          header: this.$t('components.BlipReportsTable.tags'),
          value: 'name',
        },
      },
      items: [],
      rawSeries: [],
      dialog: false,
      itemsPerPage: 5,
      requestStatus: 'loading',
      translate: {
        DIRECT_TRANSFER: {
          value: 'Transferência direta',
        },
      },
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('components.BlipReportsTable.bot'), value: 'botId' },
        { text: this.reportHeader, value: 'report' },
        { text: this.$t('components.BlipReportsTable.closedTickets'), value: 'closedTickets' },
        { text: this.$t('components.BlipReportsTable.averageFirstResponseTime'), value: 'averageFirstResponseTime' },
        { text: this.$t('components.BlipReportsTable.averageWaitTime'), value: 'averageWaitTime' },
        { text: this.$t('components.BlipReportsTable.averageResponseTime'), value: 'averageResponseTime' },
        { text: this.$t('components.BlipReportsTable.averageAttendanceTime'), value: 'averageAttendanceTime' },
      ];
    },
    mapKeys() {
      return {
        botId: this.$t('components.BlipReportsTable.bot'),
        report: this.reportHeader,
        closedTickets: this.$t('components.BlipReportsTable.closedTickets'),
        averageFirstResponseTime: this.$t('components.BlipReportsTable.averageFirstResponseTime'),
        averageWaitTime: this.$t('components.BlipReportsTable.averageWaitTime'),
        averageResponseTime: this.$t('components.BlipReportsTable.averageResponseTime'),
        averageAttendanceTime: this.$t('components.BlipReportsTable.averageAttendanceTime'),
      };
    },
    reportInfo() {
      return this.reportsTypes[this.reportType] || { header: '', value: '' };
    },
    reportHeader() {
      return this.reportInfo.header;
    },
    reportValue() {
      return this.reportInfo.value;
    },
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    hasItems() {
      return Boolean(this.items.length);
    },
    cardOptions() {
      if (this.hasDataToExport) {
        return this.baseCardOptions.concat(this.exportWithXLSXOptionsData(this.items)).sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
      }
      return this.baseCardOptions;
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getBlipService',
      'hasNoBlipBotAndContractFiltered',
      'getAppliedCardFilter',
    ]),
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (await this.hasNoBlipBotAndContractFiltered(this.itemId)) {
        this.requestStatus = 'emptyBot';
        this.items = [];
        return;
      }

      const cardFilters = await this.getAppliedCardFilter(this.itemId);

      this.getBlipService({
        servicePath: this.servicePath,
        itemId: this.itemId,
      })
        .then((responses) => {
          if (!options?.silentRefresh) {
            this.requestStatus = 'loading';
          }

          this.rawSeries = responses;

          const series = this.rawSeries.map((obj) => {
            const bot = this.getBotName(obj.to, cardFilters);
            const resourceItems = obj.resource.items.map((item) => {
              const formattedItem = {
                botId: bot,
                closedTickets: `#${item.closedTickets}`,
                averageFirstResponseTime: this.formatBlipHour(item.averageFirstResponseTime),
                averageWaitTime: this.formatBlipHour(item.averageWaitTime),
                averageResponseTime: this.formatBlipHour(item.averageResponseTime),
                averageAttendanceTime: this.formatBlipHour(item.averageAttendanceTime),
              };

              formattedItem.report = this.reportType === 'attendants'
                ? this.formatIdentity(item[this.reportInfo.value])
                : this.translateValue(item[this.reportInfo.value]);

              return formattedItem;
            });

            return resourceItems;
          }).flat();

          this.items = series;
          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.items = [];
          this.requestStatus = error.message;
        });
    },
    translateValue(value) {
      if (this.translate.hasOwnProperty(value)) {
        return this.translate[value].value;
      }

      return value;
    },
    exportWithXLSXOptionsData(items) {
      const exportExtensionOptions = this.exportExtensions.map((extension, index) => (
        {
          order: index + 1,
          name: extension.type,
          function: () => (this.exportComponent(extension, true)),
        }
      ));

      exportExtensionOptions.push({
        order: exportExtensionOptions.length + 1,
        name: 'XLSX',
        function: () => (this.exportComponentXLSX(items)),
      });

      return [
        {
          order: 5,
          name: this.$t('mixins.exportModel.label.exportLabel'),
          iconname: 'download',
          function: () => ([]),
          child: exportExtensionOptions,
          trackName: 'ST CardMenu Export Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
          isExpandedModeOption: true,
        },
      ];
    },
    exportComponentXLSX(items) {
      const translatedData = remapFieldKeys(this.mapKeys, items);
      const ws = XLSX.utils.json_to_sheet(translatedData);
      const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });
      saveAs(blob, `${this.title}.xlsx`);
    },
  },
};
</script>

<style lang="scss" scoped>

.v-select__selection.v-select__selection--comma {
  line-height: 120%;
}

.internal-style {
  width: 100%;
  display: flex;
}

.buttons {
  display: grid;
}

.clean-sla-buttons {
  box-shadow: none;
  border-radius: unset;
}

.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

::v-deep {
  .table-truncate {
    td {
      max-width: 10vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }
}
</style>
